// Who a launched lot is actually for.
//
// Project overview said `Source: MIXED` and stopped there. MIXED is true and
// useless: it means some of the lot is promised and some is not, without
// saying how much of either or to whom. The numbers are already on the job —
// `source.launch.lengthToStock` / `lengthToOrders` for the split, and the
// orders summary for the per-client share — they were simply never put
// together.
//
// STOCK first, deliberately: it is the part nobody has asked for yet, and the
// part somebody planning the next run needs to see.

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * The lot split into stock and per-client shares.
 *
 * @param job   the job document
 * @param rows  rows from `useJobOrdersSummary` ({ client, orderCode, allocatedMeters })
 * @returns {{ kind, total, stock, toOrders, clients: [{ client, orderCode, meters }] }}
 */
export function sourceBreakdown(job, rows = []) {
  const launch = job?.source?.launch || null;
  const total = num(launch?.lengthTotal) || num(job?.plannedLength);

  // One row per order LINE, so a client with two lines on one order would
  // otherwise appear twice on the badge strip.
  const byClient = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    const meters = num(r?.allocatedMeters);
    if (meters <= 0) continue;
    const client = String(r?.client || '').trim() || 'Unnamed client';
    const orderCode = String(r?.orderCode || '').trim() || null;
    const key = `${client}::${orderCode || ''}`;
    const prev = byClient.get(key);
    if (prev) prev.meters += meters;
    else byClient.set(key, { client, orderCode, meters });
  }

  const clients = [...byClient.values()].sort((a, b) => b.meters - a.meters);
  const fromRows = clients.reduce((s, c) => s + c.meters, 0);

  // The launch snapshot is the authority on the split — it is what Project
  // Bobin State and the stock figures read. The rows only name the clients.
  // When the two disagree (an allocation made before the snapshot was last
  // refreshed) the snapshot wins and the difference shows as stock.
  const toOrders = launch ? num(launch.lengthToOrders) : fromRows;
  const stock = launch
    ? num(launch.lengthToStock)
    : Math.max(0, total - fromRows);

  const kind =
    String(launch?.sourceKind || job?.source?.kind || '').toUpperCase() ||
    (toOrders > 0 && stock > 0 ? 'MIXED' : toOrders > 0 ? 'ORDER' : 'STOCK');

  return { kind, total, stock, toOrders, clients };
}

// One colour per client, stable across every screen.
//
// Three orders on one lot used to be three identical blue badges — a wall of
// text you had to read word by word. Derived from the name rather than from
// the position in the list, so the same client is the same colour on every
// product and you learn it once.
//
// `warning` is left out: STOCK owns it, and the whole point is that stock
// reads differently from a customer at a glance. On the Phoenix dark theme the
// -100 tints invert, so these are all solid variants.
//
// `secondary` is left out too — it is grey, and a grey label reads as "nothing
// here" rather than as a name. A customer always has a colour.
const CLIENT_COLOURS = ['primary', 'success', 'info', 'danger'];

export function clientBadgeColor(client) {
  const name = String(client || '').trim().toUpperCase();
  // Even an unnamed one gets a colour: grey is the thing being removed.
  if (!name) return CLIENT_COLOURS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return CLIENT_COLOURS[hash % CLIENT_COLOURS.length];
}
